import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { getHeader, getHomeData, getPartners, STRAPI_URL } from '../services/api';
import { useLang } from '../context/LanguageContext';
import { AnimatePresence, motion } from 'framer-motion';
import DynamicIcon from '../components/DynamicIcon';

const DEFAULT_SOLUTIONS = [
  { id: 1, Text: 'Yazılım', Description: 'Envanter yönetimi' },
  { id: 2, Text: 'Donanım', Description: 'Donanım takibi' },
  { id: 3, Text: 'Veri', Description: 'Veri sınıflandırma' },
  { id: 4, Text: 'Personel', Description: 'Farkındalık eğitimi' },
  { id: 5, Text: 'Belgeler', Description: 'Politika yönetimi' },
  { id: 6, Text: 'Hizmetler', Description: 'Servis yönetimi' },
  { id: 7, Text: 'Tesisler', Description: 'Tesis yönetimi' },
  { id: 8, Text: 'Ağ Cihazları', Description: 'Ağ cihazları yönetimi' },
  { id: 9, Text: 'Fiziksel Medya', Description: 'Fiziksel medya takibi' }
];

const i18nKeyMap = {
  'Yazılım': 'Yazilim',
  'Yazilim': 'Yazilim',
  'Ağ Cihazları': 'AgCihazlari',
  'Ag Cihazlari': 'AgCihazlari',
  'Fiziksel Medya': 'FizikselMedya',
  'Donanım': 'Donanim',
  'Donanim': 'Donanim',
  'Veri': 'Veri',
  'Personel': 'Personel',
  'Belgeler': 'Belgeler',
  'Tesisler': 'Tesisler',
  'Hizmetler': 'Hizmetler',
  'Medya': 'FizikselMedya',
};

const slugMap = {
  'Yazılım': '/yazilim',
  'Yazilim': '/yazilim',
  'Ağ Cihazları': '/ag-cihazlari',
  'Ag Cihazlari': '/ag-cihazlari',
  'Fiziksel Medya': '/fiziksel-medya',
  'Donanım': '/donanim',
  'Donanim': '/donanim',
  'Veri': '/veri',
  'Personel': '/personel',
  'Belgeler': '/belgeler',
  'Tesisler': '/tesisler',
  'Hizmetler': '/hizmetler',
  'Medya': '/fiziksel-medya',
};

const ikonMap = {
  'Yazılım': 'FaLaptopCode',
  'Yazilim': 'FaLaptopCode',
  'Ağ Cihazları': 'FaNetworkWired',
  'Ag Cihazlari': 'FaNetworkWired',
  'Fiziksel Medya': 'FaCompactDisc',
  'Donanım': 'FaDesktop',
  'Donanim': 'FaDesktop',
  'Veri': 'FaDatabase',
  'Personel': 'FaUsers',
  'Belgeler': 'FaFileAlt',
  'Tesisler': 'FaBuilding',
  'Hizmetler': 'FaCloud',
};

const getTitle = (item, lang = 'tr') => {
  if (lang === 'en') {
    return (
      item?.Text_en ||
      item?.text_en ||
      item?.title_en ||
      item?.Title_en ||
      item?.Text ||
      item?.text ||
      item?.Title ||
      item?.title ||
      item?.baslik ||
      ''
    );
  }

  return (
    item?.Text ||
    item?.text ||
    item?.Title ||
    item?.title ||
    item?.baslik ||
    item?.Text_en ||
    ''
  );
};

const getDesc = (item, lang = 'tr') => {
  if (lang === 'en') {
    return (
      item?.Description_en ||
      item?.description_en ||
      item?.Description ||
      item?.description ||
      item?.aciklama ||
      ''
    );
  }

  return (
    item?.Description ||
    item?.description ||
    item?.aciklama ||
    item?.Description_en ||
    ''
  );
};

const resolveSolutionsFromHomeData = (homeData) => {
  const sections = homeData?.pageContent || [];

  const moduleSection = sections.find(
    (section) => Array.isArray(section?.modules) && section.modules.length > 0
  );

  if (moduleSection?.modules?.length > 0) {
    return moduleSection.modules;
  }

  return DEFAULT_SOLUTIONS;
};

const Navbar = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { lang, toggleLang } = useLang();

  const [header, setHeader] = useState(null);
  const [demoButon, setDemoButon] = useState({ renk: '#2a9d8f' });
  const [cozumler, setCozumler] = useState(DEFAULT_SOLUTIONS);
  const [partnerler, setPartnerler] = useState([]);

  const [dropdownAcik, setDropdownAcik] = useState(false);
  const [partnersAcik, setPartnersAcik] = useState(false);
  const [modullerAcik, setModullerAcik] = useState(false);
  const [menuAcik, setMenuAcik] = useState(false);
  const [mobileCozumlerAcik, setMobileCozumlerAcik] = useState(false);
  const [mobilePartnersAcik, setMobilePartnersAcik] = useState(false);
  const [mobileModullerAcik, setMobileModullerAcik] = useState(false);

  const moduller = [
    { id: 1, title: 'Kontrol Paneli', title_en: 'Dashboard', icon: 'MdDashboard', url: '/moduller/kontrol-paneli' },
    { id: 2, title: 'Envanter Yönetimi', title_en: 'Inventory Management', icon: 'MdInventory', url: '/moduller/envanter' },
    { id: 3, title: 'Risk Yönetimi', title_en: 'Risk Management', icon: 'MdWarning', url: '/moduller/risk' },
    { id: 4, title: 'Personel Yönetimi', title_en: 'Personnel Management', icon: 'MdPeople', url: '/moduller/personel' },
    { id: 5, title: 'Politikalar ve Prosedürler', title_en: 'Policies & Procedures', icon: 'MdDescription', url: '/moduller/politikalar' },
    { id: 6, title: 'Kontrol Yönetimi', title_en: 'Control Management', icon: 'MdSecurity', url: '/moduller/kontrol' },
    { id: 7, title: 'Denetimler', title_en: 'Audits', icon: 'MdFactCheck', url: '/moduller/denetimler' },
    { id: 8, title: 'Eğitim ve Farkındalık', title_en: 'Training & Awareness', icon: 'MdSchool', url: '/moduller/egitim' },
    { id: 9, title: 'Aktivite Günlüğü', title_en: 'Activity Log', icon: 'MdTimeline', url: '/moduller/aktivite' },
    { id: 10, title: 'Raporlar', title_en: 'Reports', icon: 'MdAssessment', url: '/moduller/raporlar' },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const hData = await getHeader();
        if (hData) setHeader(hData);

        const homeData = await getHomeData();
        const resolvedSolutions = resolveSolutionsFromHomeData(homeData);
        if (Array.isArray(resolvedSolutions) && resolvedSolutions.length > 0) {
          setCozumler(resolvedSolutions);
        }

        const pData = await getPartners();
        if (Array.isArray(pData) && pData.length > 0) {
          setPartnerler(pData);
        }
      } catch (error) {
        console.error('Navbar data fetch error (CORS or Network):', error);
      }
    };

    fetchData();
  }, []);

  const logoUrl = header?.logo?.url
    ? header.logo.url.startsWith('http')
      ? header.logo.url
      : `${STRAPI_URL}${header.logo.url}`
    : null;

  const trFlag = header?.dil?.[1]?.url
    ? header.dil[1].url.startsWith('http')
      ? header.dil[1].url
      : `${STRAPI_URL}${header.dil[1].url}`
    : null;

  const enFlag = header?.dil?.[0]?.url
    ? header.dil[0].url.startsWith('http')
      ? header.dil[0].url
      : `${STRAPI_URL}${header.dil[0].url}`
    : null;

  return (
    <nav className="px-4 md:px-6 lg:px-10 xl:px-16 py-3 md:py-4 bg-white border-b border-gray-100 sticky top-0 z-50 font-poppins">
      <div className="relative flex justify-start items-center gap-6">
        {/* Elit Arge - sadece solda */}
        <div className="flex items-center gap-1.5 pr-2 border-r border-gray-200 flex-shrink-0">
          <a href="https://elitarge.com/" target="_blank" rel="noopener noreferrer">
            <img src="/elitarge-logo.webp" alt="Elit Arge" className="h-5 w-auto object-contain" />
          </a>
        </div>

        {/* RUZY - desktop'ta çizgiye yakın, mobilde ortada */}
        <Link to="/" className="flex items-center gap-2 no-underline xl:static absolute left-1/2 xl:left-auto xl:transform-none -translate-x-1/2">
          {logoUrl ? (
            <img src={logoUrl} alt="RUZY" className="w-8 h-8 md:w-10 md:h-10 object-contain" />
          ) : (
            <div className="w-8 h-8 md:w-10 md:h-10 bg-[#15614F] rounded-lg flex items-center justify-center text-white font-bold text-lg">R</div>
          )}
          <span className="text-xl md:text-2xl lg:text-3xl font-bold text-[#15614F]">
            {header?.marka || 'RUZY'}
          </span>
        </Link>

        {/* FIX 2: gap-4 2xl:gap-8 → gap-3 2xl:gap-5 — sıkışıklık giderildi */}
        <div className="hidden xl:flex items-center gap-3 2xl:gap-5 ml-auto">
          <span
            onClick={() => navigate('/iso-27001')}
            className="text-sm 2xl:text-base font-medium text-[#15614F] cursor-pointer hover:opacity-80 whitespace-nowrap"
          >
            {t('ISO 27001 Nedir?')}
          </span>

          <div
            className="relative"
            onMouseEnter={() => setDropdownAcik(true)}
            onMouseLeave={() => setDropdownAcik(false)}
          >
            <span className="text-sm 2xl:text-base font-medium text-[#15614F] cursor-pointer hover:opacity-80 whitespace-nowrap">
              {t('nav.solutions')} ▾
            </span>

            {dropdownAcik && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 bg-white rounded-xl shadow-2xl p-4 w-[650px] z-50 grid grid-cols-3 gap-2 border border-gray-50">
                {cozumler?.map((item, index) => {
                  const rawTitleTr =
                    item?.Text ||
                    item?.text ||
                    item?.Title ||
                    item?.title ||
                    item?.baslik ||
                    '';

                  const displayTitle = getTitle(item, lang).trim();
                  const displayDesc = getDesc(item, lang);

                  const i18nKey = i18nKeyMap[rawTitleTr.trim()];
                  const fallbackLabel = i18nKey ? t(`solutions.${i18nKey}`) : displayTitle;
                  const fallbackDesc = i18nKey ? t(`solutionDescs.${i18nKey}`) : displayDesc;

                  const iconName = item?.ikon || ikonMap[rawTitleTr.trim()] || 'FaCircle';

                  return (
                    <Link
                      key={item.id || index}
                      to={slugMap[rawTitleTr.trim()] || '/'}
                      onClick={() => setDropdownAcik(false)}
                      className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-[#f0faf8] transition-colors no-underline"
                    >
                      <span className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#f4fbf9] flex items-center justify-center">
                        <span className="text-[#2a9d8f]">
                          <DynamicIcon name={iconName} size={18} />
                        </span>
                      </span>

                      <div className="flex flex-col gap-0.5 min-w-0">
                        <span className="text-sm font-semibold text-[#0D3A2F] truncate">
                          {fallbackLabel}
                        </span>
                        <span className="text-xs text-[#6b9e96] line-clamp-2">
                          {fallbackDesc}
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          <div
            className="relative"
            onMouseEnter={() => setPartnersAcik(true)}
            onMouseLeave={() => setPartnersAcik(false)}
          >
            <span className="text-sm 2xl:text-base font-medium text-[#15614F] cursor-pointer hover:opacity-80 whitespace-nowrap">
              {t('nav.partners')} ▾
            </span>

            {partnersAcik && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 bg-white rounded-xl shadow-2xl p-4 w-[600px] z-50 grid grid-cols-4 gap-2 border border-gray-50">
                {partnerler.map((p, i) => {
                  const pLogoUrl = p.logo?.url
                    ? p.logo.url.startsWith('http')
                      ? p.logo.url
                      : `${STRAPI_URL}${p.logo.url}`
                    : null;

                  return (
                    <a
                      key={p.id || i}
                      href={p.url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex flex-col items-center justify-center gap-2 px-2 py-3 rounded-lg hover:bg-[#f0faf8] transition-colors no-underline"
                    >
                      {pLogoUrl ? (
                        <img
                          src={pLogoUrl}
                          alt={p.isim}
                          className="w-10 h-10 object-contain flex-shrink-0"
                        />
                      ) : (
                        <span className="text-2xl">🏢</span>
                      )}
                      <span className="text-xs font-semibold text-[#0D3A2F] text-center leading-tight">
                        {p.isim}
                      </span>
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          <div
            className="relative"
            onMouseEnter={() => setModullerAcik(true)}
            onMouseLeave={() => setModullerAcik(false)}
          >
            <span className="text-sm 2xl:text-base font-medium text-[#15614F] cursor-pointer hover:opacity-80 whitespace-nowrap">
              {lang === 'en' ? 'Modules' : 'Modüller'} ▾
            </span>

            {modullerAcik && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 bg-white rounded-xl shadow-2xl p-4 w-[420px] z-50 grid grid-cols-2 gap-2 border border-gray-50">
                {moduller.map((modul) => (
                  <Link
                    key={modul.id}
                    to={modul.url}
                    onClick={() => setModullerAcik(false)}
                    className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-[#f0faf8] transition-colors no-underline"
                  >
                    <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-[#f4fbf9] flex items-center justify-center">
                      <span className="text-[#2a9d8f]">
                        <DynamicIcon name={modul.icon} size={18} />
                      </span>
                    </span>
                    <span className="text-sm font-semibold text-[#0D3A2F]">
                      {lang === 'en' ? modul.title_en : modul.title}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <span
            onClick={() => navigate('/ruzy-ai')}
            className="text-sm 2xl:text-base font-medium text-[#15614F] cursor-pointer hover:opacity-80 whitespace-nowrap flex items-center gap-1"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#2a9d8f] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#2a9d8f]"></span>
            </span>
            RUZY AI
          </span>

          <span
            onClick={() => navigate('/neden-biz')}
            className="text-sm 2xl:text-base font-medium text-[#15614F] cursor-pointer hover:opacity-80 whitespace-nowrap"
          >
            {t('nav.whyRuzy')}
          </span>

          <span
            onClick={() => navigate('/fiyatlandirma')}
            className="text-sm 2xl:text-base font-medium text-[#15614F] cursor-pointer hover:opacity-80 whitespace-nowrap"
          >
            {t('nav.pricing')}
          </span>

          <span
            onClick={() => navigate('/iletisim')}
            className="text-sm 2xl:text-base font-medium text-[#15614F] cursor-pointer hover:opacity-80 whitespace-nowrap"
          >
            {t('nav.contact')}
          </span>

          {/* FIX 3: px-4 → px-3, py-2.5 → py-2, whitespace-nowrap eklendi */}
          <button
            onClick={() => navigate('/iletisim')}
            className="text-white font-semibold px-3 2xl:px-5 py-2 rounded-xl text-xs 2xl:text-sm border-none cursor-pointer hover:scale-105 transition-transform whitespace-nowrap"
            style={{ backgroundColor: demoButon?.renk || '#2a9d8f' }}
          >
            {t('nav.demo')}
          </button>

          {/* FIX 4: Bayrak boyutu küçültüldü w-8 h-5 → w-7 h-4 */}
          <button
            className="bg-transparent border-none p-0 cursor-pointer overflow-hidden"
            onClick={toggleLang}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={lang}
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -10, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {lang === 'tr'
                  ? trFlag
                    ? <img src={trFlag} alt="TR" className="w-7 h-4 object-cover rounded shadow-sm" />
                    : '🇹🇷'
                  : enFlag
                    ? <img src={enFlag} alt="EN" className="w-7 h-4 object-cover rounded shadow-sm" />
                    : '🇬🇧'}
              </motion.div>
            </AnimatePresence>
          </button>
        </div>

        <div className="xl:hidden flex items-center gap-3 ml-auto">
          <button
            className="bg-transparent border-none p-0 cursor-pointer overflow-hidden"
            onClick={toggleLang}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={lang}
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -10, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {lang === 'tr'
                  ? trFlag
                    ? <img src={trFlag} alt="TR" className="w-7 h-4 object-cover rounded shadow-sm" />
                    : '🇹🇷'
                  : enFlag
                    ? <img src={enFlag} alt="EN" className="w-7 h-4 object-cover rounded shadow-sm" />
                    : '🇬🇧'}
              </motion.div>
            </AnimatePresence>
          </button>

          <button
            className="bg-transparent border-none p-1 cursor-pointer text-[#15614F]"
            onClick={() => setMenuAcik(!menuAcik)}
          >
            <span className="material-symbols-outlined text-3xl">
              {menuAcik ? 'close' : 'menu'}
            </span>
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuAcik && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="xl:hidden overflow-hidden border-t border-gray-100 mt-4"
          >
            <div className="flex flex-col gap-1 py-4">
              <span
                onClick={() => {
                  navigate('/iso-27001');
                  setMenuAcik(false);
                }}
                className="px-3 py-3 text-base font-medium text-[#15614F]"
              >
                {t('ISO 27001 Nedir?')}
              </span>

              <button
                onClick={() => setMobileCozumlerAcik(!mobileCozumlerAcik)}
                className="w-full flex justify-between px-3 py-3 text-base font-medium text-[#15614F] bg-transparent border-none"
              >
                {t('nav.solutions')} <span>{mobileCozumlerAcik ? '▲' : '▼'}</span>
              </button>

              {mobileCozumlerAcik &&
                cozumler?.map((item, index) => {
                  const rawTitleTr =
                    item?.Text ||
                    item?.text ||
                    item?.Title ||
                    item?.title ||
                    item?.baslik ||
                    '';

                  const displayTitle = getTitle(item, lang).trim();

                  const i18nKey = i18nKeyMap[rawTitleTr.trim()];
                  const fallbackLabel = i18nKey ? t(`solutions.${i18nKey}`) : displayTitle;

                  const iconName = item?.ikon || ikonMap[rawTitleTr.trim()] || 'FaCircle';

                  return (
                    <Link
                      key={item.id || index}
                      to={slugMap[rawTitleTr.trim()] || '/'}
                      onClick={() => setMenuAcik(false)}
                      className="pl-8 py-2 text-sm text-[#0D3A2F] no-underline flex items-center gap-2"
                    >
                      <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                        <span className="text-[#2a9d8f]">
                          <DynamicIcon name={iconName} size={16} />
                        </span>
                      </span>
                      {fallbackLabel}
                    </Link>
                  );
                })}

              <button
                onClick={() => setMobilePartnersAcik(!mobilePartnersAcik)}
                className="w-full flex justify-between px-3 py-3 text-base font-medium text-[#15614F] bg-transparent border-none"
              >
                {t('nav.partners')} <span>{mobilePartnersAcik ? '▲' : '▼'}</span>
              </button>

              {mobilePartnersAcik &&
                partnerler.map((p, i) => {
                  const pLogoUrl = p.logo?.url
                    ? p.logo.url.startsWith('http')
                      ? p.logo.url
                      : `${STRAPI_URL}${p.logo.url}`
                    : null;

                  return (
                    <a
                      key={p.id || i}
                      href={p.url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="pl-8 py-2 text-sm text-[#0D3A2F] no-underline flex items-center gap-2"
                    >
                      {pLogoUrl ? (
                        <img src={pLogoUrl} alt={p.isim} className="w-5 h-5 object-contain" />
                      ) : (
                        '🏢'
                      )}
                      {p.isim}
                    </a>
                  );
                })}

              {/* Mobile Modüller */}
              <button
                onClick={() => setMobileModullerAcik(!mobileModullerAcik)}
                className="w-full flex justify-between px-3 py-3 text-base font-medium text-[#15614F] bg-transparent border-none"
              >
                {lang === 'en' ? 'Modules' : 'Modüller'} <span>{mobileModullerAcik ? '▲' : '▼'}</span>
              </button>

              {mobileModullerAcik &&
                moduller.map((modul) => (
                  <Link
                    key={modul.id}
                    to={modul.url}
                    onClick={() => setMenuAcik(false)}
                    className="pl-8 py-2 text-sm text-[#0D3A2F] no-underline flex items-center gap-2"
                  >
                    <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                      <span className="text-[#2a9d8f]">
                        <DynamicIcon name={modul.icon} size={16} />
                      </span>
                    </span>
                    {lang === 'en' ? modul.title_en : modul.title}
                  </Link>
                ))}

              <span
                onClick={() => {
                  navigate('/ruzy-ai');
                  setMenuAcik(false);
                }}
                className="px-3 py-3 text-base font-medium text-[#15614F] flex items-center gap-2"
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#2a9d8f] opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#2a9d8f]"></span>
                </span>
                RUZY AI
              </span>

              <span
                onClick={() => {
                  navigate('/neden-biz');
                  setMenuAcik(false);
                }}
                className="px-3 py-3 text-base font-medium text-[#15614F]"
              >
                {t('nav.whyRuzy')}
              </span>

              <span
                onClick={() => {
                  navigate('/fiyatlandirma');
                  setMenuAcik(false);
                }}
                className="px-3 py-3 text-base font-medium text-[#15614F]"
              >
                {t('nav.pricing')}
              </span>

              <span
                onClick={() => {
                  navigate('/iletisim');
                  setMenuAcik(false);
                }}
                className="px-3 py-3 text-base font-medium text-[#15614F]"
              >
                {t('nav.contact')}
              </span>

              <button
                onClick={() => navigate('/iletisim')}
                className="mx-3 mt-4 text-white font-bold py-4 rounded-xl border-none"
                style={{ backgroundColor: demoButon?.renk || '#2a9d8f' }}
              >
                {t('nav.demo')}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;